import React, { useState, useEffect, Suspense } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { CssBaseline, CircularProgress } from '@mui/material';
import { Experimental_CssVarsProvider as CssVarsProvider, useColorScheme } from '@mui/material/styles';
import { Box, AppBar, Toolbar, Tabs, Tab, Container, Button, Typography, IconButton, Tooltip, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, useMediaQuery, useTheme as useMuiTheme } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SearchIcon from '@mui/icons-material/Search';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import ArticleIcon from '@mui/icons-material/Article';
import BookIcon from '@mui/icons-material/Book';
import LogoutIcon from '@mui/icons-material/Logout';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import BugReportIcon from '@mui/icons-material/BugReport';
import PeopleIcon from '@mui/icons-material/People';
import AssignmentIcon from '@mui/icons-material/Assignment';
import SummarizeIcon from '@mui/icons-material/Summarize';
import ClientSwitcher from './components/common/ClientSwitcher';
import Chatbot from './components/chatbot/Chatbot';
import { isAuthenticated, getCurrentUser, clearAuth, getAuthToken, api } from './utils/api';
import { getApiBaseUrl } from './config/api';
import { isAdminUser as checkAdminUser } from './utils/adminAccess';
import { ClientProvider, useClient } from './contexts/ClientContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
const GenerativeSEO = React.lazy(() => import('./components/insights/GenerativeSEO'));
const PromptManager = React.lazy(() => import('./components/prompts/PromptManager'));
const SEOPerformance = React.lazy(() => import('./components/insights/SEOPerformance'));
const PageChecker = React.lazy(() => import('./components/checker/PageChecker'));
const ContentBriefGenerator = React.lazy(() => import('./components/brief/ContentBriefGenerator'));
const Settings = React.lazy(() => import('./components/settings/Settings'));
const Login = React.lazy(() => import('./components/auth/Login'));
const Glossary = React.lazy(() => import('./components/help/Glossary'));
const TestSuitePage = React.lazy(() => import('./components/admin/TestSuitePage'));
const UserManagement = React.lazy(() => import('./components/admin/UserManagement'));
const TrackedPages = React.lazy(() => import('./components/tracked-pages/TrackedPages'));
const PageAnalysisView = React.lazy(() => import('./components/tracked-pages/PageAnalysisView'));
const AEORecommendationsReport = React.lazy(() => import('./components/insights/AEORecommendationsReport'));
const QuarterlyReport = React.lazy(() => import('./components/insights/QuarterlyReport'));

const isAdminUser = checkAdminUser;

const isSuperAdminUser = (email?: string): boolean =>
  (email || '').toLowerCase().trim() === 'admin@netsertive.com';

/** Full Settings app section is admin-only; client users change password on login when required. */
const AdminSettingsGate: React.FC<{ isAdmin: boolean }> = ({ isAdmin }) => {
  const location = useLocation();
  if (!isAdmin) {
    return <Navigate to={{ pathname: '/aeo', search: location.search }} replace />;
  }
  return <Settings />;
};

// Navigation component with hamburger menu for mobile
// gscConnected here means: GSC is connected AND property is selected (seoAvailable)
const Navigation: React.FC<{ onLogout: () => void; userEmail?: string; gscConnected: boolean; isAdmin: boolean; isSuperAdmin?: boolean; quarterlyReportEnabled?: boolean }> = ({ onLogout, userEmail, gscConnected, isAdmin, isSuperAdmin, quarterlyReportEnabled = true }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const tabItems = [
    { label: 'AEO', path: '/aeo' },
    ...(gscConnected ? [{ label: 'Organic Search', path: '/seo' }] : []),
    { label: 'Analyze', path: '/checker' },
    { label: 'Content', path: '/content-brief' },
    { label: 'Tracked pages', path: '/tracked-pages' },
    ...(quarterlyReportEnabled ? [{ label: 'Website Health Report', path: '/quarterly-report' }] : []),
    { label: 'Prompt settings', path: '/prompts' }
  ];

  const getTabValue = () => {
    if (location.pathname === '/glossary' || location.pathname === '/settings' || location.pathname === '/test-suite' || location.pathname === '/user-management') return -1;
    if (location.pathname.startsWith('/tracked-pages')) {
      const idx = tabItems.findIndex((item) => item.path === '/tracked-pages');
      return idx >= 0 ? idx : -1;
    }
    const index = tabItems.findIndex((item) => item.path === location.pathname);
    if (index >= 0) return index;
    return 0;
  };

  const preserveLocationSearch = (path: string) => {
    const hasLocation = location.search && new URLSearchParams(location.search).get('location');
    return hasLocation ? `${path}${path.includes('?') ? '&' + location.search.slice(1) : location.search}` : path;
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    const target = tabItems[newValue];
    if (target) navigate(preserveLocationSearch(target.path));
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavClick = (path: string) => {
    navigate(preserveLocationSearch(path));
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const navItems = [
    { label: 'AEO', path: '/aeo', icon: <DashboardIcon /> },
    ...(gscConnected ? [{ label: 'Organic Search', path: '/seo', icon: <SearchIcon /> }] : []),
    { label: 'Analyze', path: '/checker', icon: <AnalyticsIcon /> },
    { label: 'Content', path: '/content-brief', icon: <ArticleIcon /> },
    { label: 'Tracked pages', path: '/tracked-pages', icon: <AssignmentIcon /> },
    ...(quarterlyReportEnabled ? [{ label: 'Quarterly SEO Audit', path: '/quarterly-report', icon: <SummarizeIcon /> }] : []),
    { label: 'Prompt settings', path: '/prompts', icon: <QuestionAnswerIcon /> },
    ...(isAdmin ? [{ label: 'Test Suite', path: '/test-suite', icon: <BugReportIcon /> }] : []),
    ...(isSuperAdmin ? [{ label: 'User management', path: '/user-management', icon: <PeopleIcon /> }] : []),
  ];

  // Don't redirect immediately - wait a moment for GSC status to load
  // Note: gscConnected here already means "connected AND property selected" (seoAvailable)
  useEffect(() => {
    if (location.pathname === '/seo') {
      // Give time for GSC status to load (500ms should be enough)
      const timer = setTimeout(() => {
        // SEO should only be accessible if GSC is connected AND property is selected
        if (!gscConnected) {
          navigate('/aeo', { replace: true });
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [gscConnected, location.pathname, navigate]);

  useEffect(() => {
    if (location.pathname === '/quarterly-report' && !quarterlyReportEnabled) {
      navigate('/aeo', { replace: true });
    }
  }, [location.pathname, navigate, quarterlyReportEnabled]);

  const drawer = (
    <Box sx={{ width: 280 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box
          component="img"
          src="/netsertive-logo.svg"
          alt="Netsertive"
          sx={{ height: 32, width: 'auto', cursor: 'pointer' }}
          onClick={() => handleNavClick('/')}
        />
        <IconButton onClick={handleDrawerToggle}>
          <CloseIcon />
        </IconButton>
      </Box>
      {userEmail && (
        <>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <ClientSwitcher isAdmin={isAdmin} userEmail={userEmail} />
          </Box>
          {isAdmin && (
            <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
              <SettingsIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              <Button
                fullWidth
                onClick={() => handleNavClick('/settings')}
                sx={{
                  justifyContent: 'flex-start',
                  textTransform: 'none',
                  color: location.pathname === '/settings' ? 'primary.main' : 'text.secondary',
                  fontWeight: location.pathname === '/settings' ? 600 : 400
                }}
              >
                Settings
              </Button>
            </Box>
          )}
        </>
      )}
      <List>
        {navItems.map((item) => (
          <ListItem key={item.path} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path || (item.path === '/aeo' && location.pathname === '/') || (item.path === '/tracked-pages' && location.pathname.startsWith('/tracked-pages'))}
              onClick={() => handleNavClick(item.path)}
              sx={{
                '&.Mui-selected': {
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': {
                    bgcolor: 'primary.dark',
                  },
                  '& .MuiListItemIcon-root': {
                    color: 'primary.contrastText',
                  },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          </ListItem>
        ))}
        <ListItem disablePadding>
          <ListItemButton onClick={onLogout}>
            <ListItemIcon sx={{ minWidth: 40 }}>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary="Logout" />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <>
      {/* Secondary Top Bar - Logo, Client Picker, Settings, Logout */}
      <AppBar 
        position="static" 
        elevation={0} 
        sx={{ 
          bgcolor: 'background.paper', 
          borderBottom: 1, 
          borderColor: 'divider',
          zIndex: (theme) => theme.zIndex.drawer + 1
        }}
      >
        <Toolbar 
          sx={{ 
            px: { xs: 1, sm: 2 }, 
            minHeight: { xs: 48, md: 56 },
            justifyContent: 'space-between'
          }}
        >
          {/* Left: Logo */}
          <Box
            component="img"
            src="/netsertive-logo.svg"
            alt="Netsertive"
            sx={{
              height: { xs: 28, md: 32 },
              width: 'auto',
              cursor: 'pointer',
              '&:hover': {
                opacity: 0.8,
              },
              flexShrink: 0,
              display: 'block',
            }}
            onClick={() => navigate('/')}
          />
          
          {/* Right: Client Picker, Settings, Logout */}
          {userEmail && (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: { xs: 1, sm: 2 },
              flexShrink: 0
            }}>
              {!isMobile && (
                <Box sx={{ minWidth: 200, maxWidth: 250 }}>
                  <ClientSwitcher isAdmin={isAdmin} userEmail={userEmail} />
                </Box>
              )}
              <Tooltip title="Glossary">
                <IconButton
                  size="small"
                  onClick={() => navigate('/glossary')}
                  sx={{
                    color: location.pathname === '/glossary' ? 'primary.main' : 'text.secondary',
                    border: location.pathname === '/glossary' ? '1px solid' : '1px solid transparent',
                    borderColor: location.pathname === '/glossary' ? 'primary.main' : 'transparent',
                    '&:hover': {
                      bgcolor: 'action.hover'
                    }
                  }}
                >
                  <BookIcon fontSize={isMobile ? 'medium' : 'small'} />
                </IconButton>
              </Tooltip>
              {isAdmin && (
                <Tooltip title="Test Suite">
                  <IconButton
                    size="small"
                    onClick={() => navigate('/test-suite')}
                    sx={{
                      color: location.pathname === '/test-suite' ? 'primary.main' : 'text.secondary',
                      border: location.pathname === '/test-suite' ? '1px solid' : '1px solid transparent',
                      borderColor: location.pathname === '/test-suite' ? 'primary.main' : 'transparent',
                      '&:hover': {
                        bgcolor: 'action.hover'
                      }
                    }}
                  >
                    <BugReportIcon fontSize={isMobile ? 'medium' : 'small'} />
                  </IconButton>
                </Tooltip>
              )}
              {isSuperAdmin && (
                <Tooltip title="User management">
                  <IconButton
                    size="small"
                    onClick={() => navigate('/user-management')}
                    sx={{
                      color: location.pathname === '/user-management' ? 'primary.main' : 'text.secondary',
                      border: location.pathname === '/user-management' ? '1px solid' : '1px solid transparent',
                      borderColor: location.pathname === '/user-management' ? 'primary.main' : 'transparent',
                      '&:hover': {
                        bgcolor: 'action.hover'
                      }
                    }}
                  >
                    <PeopleIcon fontSize={isMobile ? 'medium' : 'small'} />
                  </IconButton>
                </Tooltip>
              )}
              {isAdmin && (
                <Tooltip title="Settings">
                  <IconButton
                    size="small"
                    onClick={() => navigate('/settings')}
                    sx={{
                      color: location.pathname === '/settings' ? 'primary.main' : 'text.secondary',
                      border: location.pathname === '/settings' ? '1px solid' : '1px solid transparent',
                      borderColor: location.pathname === '/settings' ? 'primary.main' : 'transparent',
                      '&:hover': {
                        bgcolor: 'action.hover'
                      }
                    }}
                  >
                    <SettingsIcon fontSize={isMobile ? 'medium' : 'small'} />
                  </IconButton>
                </Tooltip>
              )}
              <Button 
                size="small" 
                onClick={onLogout} 
                variant="outlined" 
                sx={{ 
                  whiteSpace: 'nowrap',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  px: { xs: 1, sm: 2 }
                }}
              >
                Logout
              </Button>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Primary Navigation Bar - Main Tabs */}
      <AppBar 
        position="static" 
        elevation={0} 
        sx={{ 
          bgcolor: 'background.default', 
          borderBottom: 1, 
          borderColor: 'divider'
        }}
      >
        <Toolbar sx={{ px: { xs: 1, sm: 2 }, minHeight: { xs: 48, md: 56 }, justifyContent: 'center' }}>
          {isMobile ? (
            <IconButton
              color="primary"
              aria-label="open drawer"
              edge="end"
              onClick={handleDrawerToggle}
              sx={{ ml: 'auto' }}
            >
              <MenuIcon />
            </IconButton>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0, width: '100%', justifyContent: 'center' }}>
              <Tabs 
                value={getTabValue()} 
                onChange={handleTabChange} 
                sx={{ 
                  flexGrow: 0,
                  minWidth: 0,
                  '& .MuiTab-root': {
                    minWidth: { xs: 60, sm: 72 },
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    px: { xs: 1.5, sm: 2 },
                    textTransform: 'none',
                    fontWeight: 500
                  }
                }}
              >
                {tabItems.map((item) => (
                  <Tab key={item.path} label={item.label} />
                ))}
              </Tabs>
            </Box>
          )}
        </Toolbar>
      </AppBar>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 280,
          },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

const AppShell: React.FC<{ onLogout: () => void; userEmail?: string }> = ({ onLogout, userEmail }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedClient, setSelectedClient, setSelectedLocation, locationRestricted, quarterlyReportEnabled } = useClient();
  const [gscConnected, setGscConnected] = useState(false);

  // Sync ?location= URL param to context so single-location / franchise views are shareable (e.g. /aeo?location=NewYork). Skip when user is locked to one location.
  useEffect(() => {
    if (locationRestricted) return;
    const params = new URLSearchParams(location.search);
    const locationParam = params.get('location');
    if (!selectedClient) return;

    if (!selectedClient.locations?.length) {
      if (locationParam) {
        params.delete('location');
        navigate({ pathname: location.pathname, search: params.toString() ? `?${params.toString()}` : '' }, { replace: true });
      }
      return;
    }

    if (!locationParam) return;
    const valid = selectedClient.locations.some((loc) => loc.location === locationParam);
    if (valid) setSelectedLocation(locationParam);
  }, [
    location.pathname,
    location.search,
    selectedClient,
    selectedClient?.clientName,
    selectedClient?.locations,
    setSelectedLocation,
    locationRestricted,
    navigate
  ]);
  const [gscNeedsPropertySelection, setGscNeedsPropertySelection] = useState(false);
  const clientName = selectedClient?.clientName || '';
  const isAdmin = isAdminUser(userEmail);
  const isSuperAdmin = isSuperAdminUser(userEmail);

  // When we navigated here with addedClient (after adding a client), ensure context is set so all screens use that client
  useEffect(() => {
    const added = (location.state as { addedClient?: { clientName: string; name?: string; primaryDomain?: string; domains?: string[]; promptCount?: number; competitors?: unknown[]; businessSummary?: string } })?.addedClient;
    if (added?.clientName && selectedClient?.clientName !== added.clientName) {
      const fullClient = {
        clientName: added.clientName,
        name: added.name ?? added.clientName,
        primaryDomain: added.primaryDomain ?? '',
        promptCount: added.promptCount ?? 0,
        domains: added.domains ?? (added.primaryDomain ? [added.primaryDomain] : []),
        competitors: (added.competitors ?? []) as Array<{ name: string; urls: string[] }>,
        businessSummary: added.businessSummary ?? ''
      };
      setSelectedClient(fullClient);
      localStorage.setItem('selectedClientName', added.clientName);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate, selectedClient?.clientName, setSelectedClient]);

  const loadGscStatus = React.useCallback(() => {
    if (!clientName) {
      setGscConnected(false);
      setGscNeedsPropertySelection(false);
      return;
    }

    api.get(`/api/gsc/status?clientName=${encodeURIComponent(clientName)}`)
      .then((status) => {
        const connected = !!status?.connected;
        // Property is selected if siteUrl exists
        // needsPropertySelection is true when siteUrl is missing
        const hasProperty = !!status?.siteUrl;
        const needsSelection = !hasProperty;
        
        setGscConnected(connected);
        setGscNeedsPropertySelection(needsSelection);
      })
      .catch(() => {
        setGscConnected(false);
        setGscNeedsPropertySelection(false);
      });
  }, [clientName]);

  useEffect(() => {
    loadGscStatus();
  }, [loadGscStatus]);

  // Listen for GSC status updates (e.g., when site is saved in Settings)
  useEffect(() => {
    const handleGscStatusUpdate = () => {
      console.log('[AppShell] GSC status update event received, refreshing...');
      loadGscStatus();
    };

    window.addEventListener('gsc-status-updated', handleGscStatusUpdate);
    return () => {
      window.removeEventListener('gsc-status-updated', handleGscStatusUpdate);
    };
  }, [loadGscStatus]);
  
  // SEO should only be available if GSC is connected AND property is selected
  const seoAvailable = gscConnected && !gscNeedsPropertySelection;

  // Check if we're in staging environment
  const isStaging = typeof window !== 'undefined' && (
    window.location.hostname.includes('staging') ||
    window.location.hostname.startsWith('staging-dot') ||
    window.location.hostname === 'staging-dot-avian-curve-466318-t4.uc.r.appspot.com' ||
    process.env.REACT_APP_ENV === 'staging'
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Staging Environment Banner */}
      {isStaging && (
        <Box
          sx={{
            bgcolor: 'warning.main',
            color: 'warning.contrastText',
            py: 0.75,
            px: 2,
            textAlign: 'center',
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
            fontWeight: 500,
            position: 'sticky',
            top: 0,
            zIndex: 1300
          }}
        >
          Staging Environment
        </Box>
      )}
      <Navigation onLogout={onLogout} userEmail={userEmail} gscConnected={seoAvailable} isAdmin={isAdmin} isSuperAdmin={isSuperAdmin} quarterlyReportEnabled={quarterlyReportEnabled} />
      <Container maxWidth={false} sx={{ flexGrow: 1, py: 3, px: { xs: 2, sm: 3 } }}>
        <Suspense
          fallback={
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
              <CircularProgress />
            </Box>
          }
        >
          <Routes>
            <Route path="/" element={<Navigate to="/aeo" replace />} />
            <Route path="/aeo/recommendations-report" element={
              <ProtectedRoute>
                <AEORecommendationsReport />
              </ProtectedRoute>
            } />
            <Route path="/aeo" element={
              <ProtectedRoute>
                <GenerativeSEO />
              </ProtectedRoute>
            } />
            <Route path="/prompts" element={
              <ProtectedRoute>
                <PromptManager />
              </ProtectedRoute>
            } />
            <Route path="/content-brief" element={
              <ProtectedRoute>
                <ContentBriefGenerator />
              </ProtectedRoute>
            } />
            <Route path="/seo" element={
              <ProtectedRoute>
                {seoAvailable ? <SEOPerformance /> : <Navigate to="/aeo" replace />}
              </ProtectedRoute>
            } />
            <Route path="/discovery" element={<Navigate to="/aeo" replace />} />
            <Route path="/checker" element={
              <ProtectedRoute>
                <PageChecker />
              </ProtectedRoute>
            } />
            <Route path="/tracked-pages" element={
              <ProtectedRoute>
                <TrackedPages />
              </ProtectedRoute>
            } />
            <Route path="/tracked-pages/analysis" element={
              <ProtectedRoute>
                <PageAnalysisView />
              </ProtectedRoute>
            } />
            <Route path="/quarterly-report" element={
              <ProtectedRoute>
                <QuarterlyReport />
              </ProtectedRoute>
            } />
            <Route path="/settings" element={
              <ProtectedRoute>
                <AdminSettingsGate isAdmin={isAdmin} />
              </ProtectedRoute>
            } />
            <Route path="/glossary" element={
              <ProtectedRoute>
                <Glossary />
              </ProtectedRoute>
            } />
            <Route path="/test-suite" element={
              <ProtectedRoute>
                {isAdmin ? <TestSuitePage /> : <Navigate to="/aeo" replace />}
              </ProtectedRoute>
            } />
            <Route path="/user-management" element={
              <ProtectedRoute>
                {isSuperAdmin ? <UserManagement /> : <Navigate to="/aeo" replace />}
              </ProtectedRoute>
            } />
          </Routes>
        </Suspense>
      </Container>
      <Chatbot />
    </Box>
  );
};

// Protected route component - preserves current path in returnTo so refresh/auth doesn't lose the page
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const [checking, setChecking] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      if (!isAuthenticated()) {
        setAuthenticated(false);
        setChecking(false);
        return;
      }

      try {
        // Verify token is still valid
        await api.get('/api/auth/me');
        setAuthenticated(true);
      } catch (error) {
        clearAuth();
        setAuthenticated(false);
      } finally {
        setChecking(false);
      }
    };

    checkAuth();
    
    // Also listen for storage changes (in case login happens in another tab/window)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'authToken') {
        checkAuth();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);
  
  // Re-check authentication when token changes (for same-tab login)
  useEffect(() => {
    const token = getAuthToken();
    if (token && !authenticated && !checking) {
      const checkAuth = async () => {
        try {
          await api.get('/api/auth/me');
          setAuthenticated(true);
        } catch (error) {
          clearAuth();
          setAuthenticated(false);
        }
      };
      checkAuth();
    }
  }, [authenticated, checking]);

  if (checking) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (!authenticated) {
    const returnTo = location.pathname + location.search;
    const loginPath = returnTo && returnTo !== '/' && returnTo !== '/aeo'
      ? `/login?returnTo=${encodeURIComponent(returnTo)}`
      : '/login';
    return <Navigate to={loginPath} replace />;
  }

  return <>{children}</>;
};

const LoginOrRedirect: React.FC<{ onLoginSuccess: (token: string, userData: { id: number; email: string }, returnTo?: string) => void }> = ({ onLoginSuccess }) => {
  const location = useLocation();
  const returnTo = React.useMemo(() => {
    const q = new URLSearchParams(location.search);
    const r = q.get('returnTo');
    if (!r || !r.startsWith('/') || r.startsWith('//')) return null;
    return r;
  }, [location.search]);
  if (isAuthenticated()) {
    return <Navigate to={returnTo || '/aeo'} replace />;
  }
  return <Login onLoginSuccess={onLoginSuccess} returnTo={returnTo ?? undefined} />;
};

const AppContentInner: React.FC<{ user: { id: number; email: string } | null; onLoginSuccess: (token: string, userData: { id: number; email: string }, returnTo?: string) => void; onLogout: () => void }> = ({ user, onLoginSuccess, onLogout }) => {
  const { isDarkMode } = useTheme();
  const { setMode } = useColorScheme();

  useEffect(() => {
    setMode(isDarkMode ? 'dark' : 'light');
  }, [isDarkMode, setMode]);

  return (
    <>
      <CssBaseline />
      <Suspense
        fallback={
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
            <CircularProgress />
          </Box>
        }
      >
        <Routes>
          <Route path="/login" element={
            <LoginOrRedirect onLoginSuccess={onLoginSuccess} />
          } />
          <Route path="/*" element={
            <ClientProvider authUserEmail={user?.email}>
              <AppShell onLogout={onLogout} userEmail={user?.email} />
            </ClientProvider>
          } />
        </Routes>
      </Suspense>
    </>
  );
};

const AppContent: React.FC = () => {
  const { theme } = useTheme();
  const [user, setUser] = useState<{ id: number; email: string } | null>(getCurrentUser());
  const navigate = useNavigate();
  const location = useLocation();

  // If user landed on frontend at /api/gsc/oauth/callback (wrong URL), redirect to backend so it can process the code and redirect to /settings
  const isGscCallbackOnFrontend = location.pathname === '/api/gsc/oauth/callback';
  const gscCallbackParams = isGscCallbackOnFrontend && (new URLSearchParams(location.search).get('code') || new URLSearchParams(location.search).get('state'));
  useEffect(() => {
    if (!isGscCallbackOnFrontend || !gscCallbackParams) return;
    const backendCallback = `${getApiBaseUrl()}/api/gsc/oauth/callback${location.search ? `?${location.search}` : ''}`;
    window.location.replace(backendCallback);
  }, [isGscCallbackOnFrontend, gscCallbackParams, location.search]);

  useEffect(() => {
    // Check if user is authenticated on mount
    const currentUser = getCurrentUser();
    if (currentUser) {
      setUser(currentUser);
    }
    
    // Check for GSC OAuth redirect params in URL
    const urlParams = new URLSearchParams(window.location.search);
    const gscConnected = urlParams.get('gsc_connected');
    const selectProperty = urlParams.get('select_property');
    const client = urlParams.get('client');
    
    if (gscConnected === 'true' && currentUser) {
      // User is authenticated and GSC was just connected
      // Navigate to settings with the query params
      const settingsPath = `/settings?gsc_connected=true&select_property=${selectProperty || 'true'}${client ? `&client=${encodeURIComponent(client)}` : ''}`;
      navigate(settingsPath, { replace: true });
    }
  }, [navigate]);

  const handleLoginSuccess = (token: string, userData: { id: number; email: string }, returnTo?: string) => {
    // Ensure token and user are set in localStorage first
    localStorage.setItem('authToken', token);
    localStorage.setItem('user', JSON.stringify(userData));
    
    // Update state
    setUser(userData);
    
    // Navigate to returnTo (from refresh/auth redirect) or default /aeo
    const target = returnTo && returnTo.startsWith('/') && !returnTo.startsWith('//') ? returnTo : '/aeo';
    setTimeout(() => {
      navigate(target, { replace: true });
    }, 100);
  };

  const handleLogout = () => {
    clearAuth();
    setUser(null);
    navigate('/login');
  };

  // Show a short message while redirecting from wrong GSC callback URL to backend
  if (isGscCallbackOnFrontend && gscCallbackParams) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', gap: 2 }}>
        <CircularProgress />
        <Typography variant="body1" color="text.secondary">Redirecting to complete GSC connection...</Typography>
      </Box>
    );
  }

  return (
    <CssVarsProvider theme={theme}>
      <AppContentInner user={user} onLoginSuccess={handleLoginSuccess} onLogout={handleLogout} />
    </CssVarsProvider>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider>
      <AppContent />
    </ThemeProvider>
  );
};

export default App;


